import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect } from 'react';
import { useRoleLabels } from '../contexts/RoleLabelsContext';
import { AlertCircle, Edit2, Trash2, Save, Briefcase, Percent, ChevronDown, ChevronRight, Users, X } from 'lucide-react';
import Sidebar from './Sidebar';
import IntroducerTeamInvitePanel from './IntroducerTeamInvitePanel';

// ── Design tokens ──────────────────────────────────────────────────────────
const NAVY   = '#0e1b2c';
const GREEN  = '#10b981';
const BORDER = '#E2E8F0';
const MUTED  = '#9ca3af';
const TEXT   = '#374151';

const LEVEL_CONFIG = {
  company: { label: 'Introducer Company', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  manager: { label: 'Introducer Manager', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  adviser: { label: 'Introducer Adviser', color: '#0f766e', bg: '#f0fdfa', border: '#99f6e4' },
  solo:    { label: 'Solo',          color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
};

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: `1px solid ${BORDER}`, borderRadius: 8,
  fontSize: 14, color: NAVY, boxSizing: 'border-box', outline: 'none',
  background: '#fff',
};

// ── Pure display components (all OUTSIDE main component) ───────────────────

function LevelBadge({ level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.solo;
  const { labelForTier } = useRoleLabels();
  return (
    <span style={{
      display: 'inline-block', background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, borderRadius: 20,
      padding: '2px 10px', fontSize: 11, fontWeight: 600,
    }}>
      {labelForTier('introducer', level)}
    </span>
  );
}

function Card({ title, icon, action, children }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon} {title}
        </h2>
        {action}
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );
}

function ActionBtn({ onClick, color, children, type = 'button', size = 'sm', disabled }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: size === 'sm' ? '6px 12px' : '9px 18px',
      fontSize: size === 'sm' ? 12 : 14, fontWeight: 600,
      background: disabled ? '#d1d5db' : color, color: '#fff',
      border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
    }}>
      {children}
    </button>
  );
}

function OutlineBtn({ onClick, children, type = 'button' }) {
  return (
    <button type={type} onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '6px 12px', fontSize: 12, fontWeight: 600,
      background: 'none', color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer',
    }}>
      {children}
    </button>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: NAVY }}>{label}</label>
      {children}
    </div>
  );
}

function MemberRow({ member, onEdit, onDelete, canEdit }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 0', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: `linear-gradient(135deg, ${NAVY}, #1e3a5f)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0,
        }}>
          {member.name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{member.name}</div>
          <div style={{ fontSize: 12, color: MUTED }}>{member.email}</div>
          {member.status === 'inactive' && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Inactive</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <LevelBadge level={member.introducer_level} />
        {canEdit && (
          <>
            <ActionBtn onClick={() => onEdit(member)} color='#3b82f6'><Edit2 size={13} /> Edit</ActionBtn>
            <ActionBtn onClick={() => onDelete(member.id)} color='#ef4444'><Trash2 size={13} /> Remove</ActionBtn>
          </>
        )}
      </div>
    </div>
  );
}

function SplitBar({ company, manager, sales }) {
  const total = company + manager + sales;
  const cPct = total > 0 ? (company / total) * 100 : 0;
  const mPct = total > 0 ? (manager / total) * 100 : 0;
  const sPct = total > 0 ? (sales   / total) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ width: `${cPct}%`, background: GREEN }} />
        <div style={{ width: `${mPct}%`, background: '#3b82f6' }} />
        <div style={{ width: `${sPct}%`, background: '#8b5cf6' }} />
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
        {[
          { label: 'Company', val: company, bg: GREEN },
          manager > 0 && { label: 'Manager', val: manager, bg: '#3b82f6' },
          { label: 'Adviser', val: sales, bg: '#8b5cf6' },
        ].filter(Boolean).map(item => (
          <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: item.bg, display: 'inline-block' }} />
            <span style={{ color: MUTED }}>{item.label}</span>
            <strong style={{ color: NAVY }}>{item.val}%</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── TeamSection: list only. Adding people is done via email invite (panel
// above); this just lists current members with Edit/Remove. ─────────────────
function TeamSection({ type, members, isAdviser, onEditMember, onDelete }) {
  const isManager = type === 'manager';
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{
          background: isManager ? '#eff6ff' : '#f0fdfa',
          color: isManager ? '#1d4ed8' : '#0f766e',
          borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
        }}>
          {isManager ? 'Managers' : 'Advisers'} · {members.length}
        </span>
      </div>
      {members.length === 0 ? (
        <div style={{
          padding: '24px', textAlign: 'center', background: '#f8fafc',
          borderRadius: 10, border: `1px dashed ${BORDER}`, color: MUTED, fontSize: 13,
        }}>
          No {isManager ? 'managers' : 'advisers'} yet — invite one above to get started
        </div>
      ) : (
        <div>
          {members.map(m => (
            <MemberRow key={m.id} member={m} canEdit={!isAdviser} onEdit={onEditMember} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── MemberForm: standalone card form (OUTSIDE main component) ─────────────
function MemberForm({ editingId, formData, isCompany, mobile, onChange, onRoleChange, onSubmit, onCancel }) {
  const isEdit = !!editingId;
  return (
    <div style={{ background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '20px', marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: NAVY }}>
          {isEdit ? 'Edit Team Member' : 'Add New Team Member'}
        </h3>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, display: 'flex' }}>
          <X size={18} />
        </button>
      </div>
      <form onSubmit={onSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 4 }}>
          <FormField label="Full Name">
            <input
              type="text"
              value={formData.name}
              onChange={e => onChange({ name: e.target.value })}
              required
              style={inputStyle}
              placeholder="e.g. Jane Smith"
              autoFocus
            />
          </FormField>
          <FormField label="Email Address">
            <input
              type="email"
              value={formData.email}
              onChange={e => onChange({ email: e.target.value })}
              disabled={isEdit}
              required={!isEdit}
              style={{ ...inputStyle, background: isEdit ? '#f1f5f9' : '#fff', color: isEdit ? MUTED : NAVY }}
              placeholder="jane@example.com"
            />
          </FormField>
          <FormField label="Role">
            {isCompany ? (
              <select
                value={formData.role}
                onChange={e => onRoleChange(e.target.value)}
                style={{ ...inputStyle }}
                required
              >
                <option value="adviser">Adviser</option>
                <option value="manager">Manager</option>
              </select>
            ) : (
              <div style={{ ...inputStyle, background: '#f1f5f9', color: MUTED }}>
                Adviser
              </div>
            )}
          </FormField>
        </div>
        {isCompany && formData.role === 'manager' && (
          <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#1d4ed8' }}>
            Managers can add and manage advisers on their own team.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <ActionBtn type="submit" color={GREEN} size="md">
            <Save size={14} /> {isEdit ? 'Save Changes' : 'Add Member'}
          </ActionBtn>
          <OutlineBtn onClick={onCancel}>Cancel</OutlineBtn>
        </div>
      </form>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
const IntroducerTeamManager = () => {
  const mobile = useMobile();

  const [teamMembers, setTeamMembers] = useState([]);
  const [company, setCompany]         = useState(null);
  const [viewerLevel, setViewerLevel] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const [showForm, setShowForm]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData]   = useState({ name: '', email: '', role: 'adviser' });

  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '', type: '', contact_email: '', contact_phone: '',
    allow_fee_override: true, require_override_reason: false,
  });

  const [commissionStructure, setCommissionStructure] = useState(null);
  const [managerCount, setManagerCount] = useState(0);
  const [showSplitForm, setShowSplitForm] = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const [splitForm, setSplitForm] = useState({ company_allocation: '', manager_allocation: '', sales_rep_allocation: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchTeamMembers(), fetchCompany(), fetchCommission()]);
    setLoading(false);
  };

  const fetchCommission = async () => {
    try {
      const res = await fetch('/api/introducer/commission-structure', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) {
        const data = await res.json();
        setCommissionStructure(data);
        setManagerCount(data.manager_count || 0);
        if (data.current) setSplitForm({ company_allocation: data.current.company_allocation, manager_allocation: data.current.manager_allocation, sales_rep_allocation: data.current.sales_rep_allocation });
      }
    } catch {}
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await fetch('/api/introducer/team', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { setTeamMembers(await res.json()); setError(''); }
      else { const d = await res.json().catch(() => ({})); setError(d.message || 'Failed to fetch team'); }
    } catch (err) { setError(err.message); }
  };

  const fetchCompany = async () => {
    try {
      const res = await fetch('/api/introducer/company', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) {
        const data = await res.json();
        setCompany(data);
        if (data) {
          setViewerLevel(data.viewer_level);
          setCompanyForm({ name: data.name || '', type: data.type || '', contact_email: data.contact_email || '', contact_phone: data.contact_phone || '', allow_fee_override: data.allow_fee_override ?? true, require_override_reason: data.require_override_reason ?? false });
        }
      }
    } catch {}
  };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  // Edit-only now. New members are added via email invite (IntroducerTeamInvitePanel).
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const body = { name: formData.name, introducer_level: formData.role };
      const res = await fetch(`/api/introducer/team/${editingId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (res.ok) { flash('Team member updated'); fetchTeamMembers(); resetForm(); }
      else { const d = await res.json().catch(() => ({})); setError(d.message || 'Failed to update'); }
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Remove this team member? They will be marked inactive.')) return;
    try {
      const res = await fetch(`/api/introducer/team/${memberId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { flash('Team member removed'); fetchTeamMembers(); }
      else { const d = await res.json().catch(() => ({})); setError(d.message || 'Failed to remove'); }
    } catch (err) { setError(err.message); }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/introducer/company', { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(companyForm) });
      if (res.ok) { flash('Company details saved'); fetchCompany(); setShowCompanyForm(false); }
      else { const d = await res.json().catch(() => ({})); setError(d.message || 'Failed to save'); }
    } catch (err) { setError(err.message); }
  };

  const handleSplitSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = { company_allocation: Number(splitForm.company_allocation) || 0, manager_allocation: managerCount > 0 ? (Number(splitForm.manager_allocation) || 0) : 0, sales_rep_allocation: Number(splitForm.sales_rep_allocation) || 0 };
      const res = await fetch('/api/introducer/commission-structure', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(body) });
      if (res.ok) { flash('Commission split updated'); setShowSplitForm(false); fetchCommission(); }
      else { const d = await res.json().catch(() => ({})); setError(d.message || 'Failed to update split'); }
    } catch (err) { setError(err.message); }
  };

  const resetForm = () => { setShowForm(false); setEditingId(null); setFormData({ name: '', email: '', role: 'adviser' }); };

  const openEditForm = (member) => {
    setEditingId(member.id);
    setFormData({ name: member.name, email: member.email, role: member.introducer_level });
    setShowForm(true);
  };

  const managers = teamMembers.filter(m => m.introducer_level === 'manager');
  const advisers = teamMembers.filter(m => m.introducer_level === 'adviser');

  const isCompany = viewerLevel === 'company';
  const isSolo    = viewerLevel === 'solo' || viewerLevel === null;
  const isAdviser = viewerLevel === 'adviser';

  const splitTotal = Math.round(((Number(splitForm.company_allocation) || 0) + (managerCount > 0 ? (Number(splitForm.manager_allocation) || 0) : 0) + (Number(splitForm.sales_rep_allocation) || 0)) * 100) / 100;
  const splitValid = splitTotal === 100;

  const ml = mobile ? 0 : 70;

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: ml, padding: mobile ? '64px 16px 32px' : '32px 24px', minHeight: '100vh', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900 }}>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: NAVY }}>Team & Users</h1>
              {viewerLevel && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: (LEVEL_CONFIG[viewerLevel] || LEVEL_CONFIG.solo).bg, color: (LEVEL_CONFIG[viewerLevel] || LEVEL_CONFIG.solo).color, border: `1px solid ${(LEVEL_CONFIG[viewerLevel] || LEVEL_CONFIG.solo).border}`, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                  {(LEVEL_CONFIG[viewerLevel] || LEVEL_CONFIG.solo).label}
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>
              {isCompany ? 'Manage your introducer company, team, and commission structure'
                : viewerLevel === 'manager' ? 'Manage your advisers and commission distribution'
                : isSolo    ? 'Invite your first team member to upgrade to a company account'
                : 'View your team'}
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: 10, marginBottom: 20, fontSize: 13 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
              <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          )}
          {success && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 16px', background: '#d1fae5', color: '#065f46', borderRadius: 10, marginBottom: 20, fontSize: 13, fontWeight: 600 }}>
              ✓ {success}
            </div>
          )}

          {loading ? <div style={{ color: MUTED, fontSize: 14, padding: 32 }}>Loading…</div> : (
            <>
              {/* Solo upgrade prompt */}
              {isSolo && !company && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20 }}>⭐</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>You're a solo introducer</div>
                    <div style={{ fontSize: 13, color: '#a16207' }}>Invite your first team member below to automatically upgrade to a company account and unlock commission splits.</div>
                  </div>
                </div>
              )}

              {/* Company Details */}
              {company && (
                <Card title="Company Details" icon={<Briefcase size={16} />}
                  action={company.can_edit && !showCompanyForm ? <ActionBtn onClick={() => setShowCompanyForm(true)} color='#3b82f6'><Edit2 size={13} /> Edit</ActionBtn> : null}
                >
                  {!showCompanyForm ? (
                    <>
                      {company.broker_name && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 16 }}>
                          <span style={{ fontSize: 20 }}>🏦</span>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Introducing for</div>
                            <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{company.broker_name}</div>
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                        {[
                          { label: 'Company Name',   value: company.name },
                          { label: 'Type',           value: company.type || '—' },
                          { label: 'Introducer Fee', value: company.fee_type === 'fixed' && company.fixed_fee ? `£${company.fixed_fee}` : company.fee_type === 'percentage' && company.fee_percentage ? `${company.fee_percentage}%` : company.fee_type === 'none' ? 'None' : '—' },
                          { label: 'Contact Email',  value: company.contact_email || '—' },
                          { label: 'Contact Phone',  value: company.contact_phone || '—' },
                        ].map(f => (
                          <div key={f.label} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.label}</div>
                            <div style={{ fontWeight: 600, color: NAVY, fontSize: 14 }}>{f.value}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleCompanySubmit}>
                      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <FormField label="Company Name">
                          <input type="text" value={companyForm.name} onChange={e => setCompanyForm(p => ({ ...p, name: e.target.value }))} required style={inputStyle} />
                        </FormField>
                        <FormField label="Type (e.g. Estate Agent)">
                          <input type="text" value={companyForm.type} onChange={e => setCompanyForm(p => ({ ...p, type: e.target.value }))} style={inputStyle} />
                        </FormField>
                        <FormField label="Introducer Fee (broker-set, read only)">
                          <div style={{ padding: '9px 12px', border: `1px solid ${BORDER}`, borderRadius: 8, background: '#f8fafc', fontSize: 14, color: TEXT, fontWeight: 600 }}>
                            {company.fee_type === 'fixed' && company.fixed_fee ? `£${company.fixed_fee}` : company.fee_type === 'percentage' && company.fee_percentage ? `${company.fee_percentage}%` : company.fee_type || '—'}
                          </div>
                        </FormField>
                        <FormField label="Contact Email">
                          <input type="email" value={companyForm.contact_email} onChange={e => setCompanyForm(p => ({ ...p, contact_email: e.target.value }))} style={inputStyle} />
                        </FormField>
                        <FormField label="Contact Phone">
                          <input type="tel" value={companyForm.contact_phone} onChange={e => setCompanyForm(p => ({ ...p, contact_phone: e.target.value }))} style={inputStyle} />
                        </FormField>
                      </div>
                      <div style={{ display: 'flex', gap: 16, margin: '12px 0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={companyForm.allow_fee_override} onChange={e => setCompanyForm(p => ({ ...p, allow_fee_override: e.target.checked }))} /> Allow fee override
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={companyForm.require_override_reason} onChange={e => setCompanyForm(p => ({ ...p, require_override_reason: e.target.checked }))} /> Require override reason
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <ActionBtn type="submit" color={GREEN} size="md"><Save size={14} /> Save</ActionBtn>
                        <OutlineBtn onClick={() => setShowCompanyForm(false)}>Cancel</OutlineBtn>
                      </div>
                    </form>
                  )}
                </Card>
              )}

              {/* Commission Distribution */}
              {commissionStructure?.current && (
                <Card title="Commission Distribution" icon={<Percent size={16} />}
                  action={commissionStructure.can_edit && !showSplitForm ? <ActionBtn onClick={() => setShowSplitForm(true)} color='#3b82f6'><Edit2 size={13} /> Edit Split</ActionBtn> : null}
                >
                  <p style={{ margin: '0 0 16px', fontSize: 13, color: MUTED }}>How the broker fee is distributed within your organisation. Total must equal 100%.</p>
                  {managerCount > 0 && Number(commissionStructure.current.manager_allocation) === 0 && commissionStructure.can_edit && (
                    <div style={{ display: 'flex', gap: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                      <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                      You have {managerCount} active manager{managerCount > 1 ? 's' : ''} but 0% is allocated to the manager tier.
                    </div>
                  )}
                  {!showSplitForm ? (
                    <SplitBar company={Number(commissionStructure.current.company_allocation)} manager={Number(commissionStructure.current.manager_allocation)} sales={Number(commissionStructure.current.sales_rep_allocation)} />
                  ) : (
                    <form onSubmit={handleSplitSubmit}>
                      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
                        <FormField label="Company (%)">
                          <input type="number" min="0" max="100" step="0.01" value={splitForm.company_allocation} onChange={e => setSplitForm(p => ({ ...p, company_allocation: e.target.value }))} required style={inputStyle} />
                        </FormField>
                        {managerCount > 0 ? (
                          <FormField label={`Manager (%) — ${managerCount} active`}>
                            <input type="number" min="0" max="100" step="0.01" value={splitForm.manager_allocation} onChange={e => setSplitForm(p => ({ ...p, manager_allocation: e.target.value }))} required style={inputStyle} />
                          </FormField>
                        ) : (
                          <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: 13, color: MUTED, display: 'flex', alignItems: 'center' }}>Add a manager to enable manager allocation</div>
                        )}
                        <FormField label="Adviser (%)">
                          <input type="number" min="0" max="100" step="0.01" value={splitForm.sales_rep_allocation} onChange={e => setSplitForm(p => ({ ...p, sales_rep_allocation: e.target.value }))} required style={inputStyle} />
                        </FormField>
                      </div>
                      <div style={{ padding: '10px 16px', borderRadius: 8, textAlign: 'center', fontWeight: 700, fontSize: 14, marginBottom: 16, background: splitValid ? '#d1fae5' : '#fee2e2', color: splitValid ? '#065f46' : '#991b1b' }}>
                        Total: {splitTotal}% {splitValid ? '✓' : '— must equal 100%'}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <ActionBtn type="submit" color={GREEN} size="md"><Save size={14} /> Save Split</ActionBtn>
                        <OutlineBtn onClick={() => setShowSplitForm(false)}>Cancel</OutlineBtn>
                      </div>
                    </form>
                  )}
                  {commissionStructure.history?.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <button onClick={() => setShowHistory(!showHistory)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>
                        {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Previous structures ({commissionStructure.history.length})
                      </button>
                      {showHistory && (
                        <div style={{ marginTop: 8 }}>
                          {commissionStructure.history.map(h => (
                            <div key={h.id} style={{ padding: '8px 12px', borderRadius: 6, background: '#f8fafc', border: `1px solid ${BORDER}`, marginBottom: 6, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                              <strong style={{ color: NAVY }}>{h.company_allocation}% / {h.manager_allocation}% / {h.sales_rep_allocation}%</strong>
                              <span style={{ color: MUTED }}>{new Date(h.effective_from).toLocaleDateString()} → {h.effective_to ? new Date(h.effective_to).toLocaleDateString() : 'present'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Team Members */}
              <Card title="Team Members" icon={<Users size={16} />}>
                {isAdviser ? (
                  <div style={{ color: MUTED, fontSize: 13, textAlign: 'center', padding: 24 }}>Contact your manager to update team membership.</div>
                ) : (
                  <>
                    {/* Email-invite panel — the single way to add new teammates */}
                    <IntroducerTeamInvitePanel managers={managers} canChooseRole={isCompany || isSolo} />

                    {/* Inline edit form (name/role only — no password, no direct add) */}
                    {showForm && editingId && (
                      <MemberForm
                        editingId={editingId}
                        formData={formData}
                        isCompany={isCompany}
                        mobile={mobile}
                        onChange={patch => setFormData(prev => ({ ...prev, ...patch }))}
                        onRoleChange={role => setFormData(prev => ({ ...prev, role }))}
                        onSubmit={handleSubmit}
                        onCancel={resetForm}
                      />
                    )}

                    {/* Manager section — company owners only */}
                    {isCompany && (
                      <>
                        <TeamSection
                          type="manager"
                          members={managers}
                          isAdviser={false}
                          onEditMember={openEditForm}
                          onDelete={handleDelete}
                        />
                        <div style={{ borderTop: `1px solid ${BORDER}`, margin: '20px 0' }} />
                      </>
                    )}

                    {/* Adviser section */}
                    <TeamSection
                      type="adviser"
                      members={advisers}
                      isAdviser={false}
                      onEditMember={openEditForm}
                      onDelete={handleDelete}
                    />
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default IntroducerTeamManager;
